import { parseTimeStampOrNull } from '@/lib/parseTimeStamp';
import { differenceInCalendarDays } from 'date-fns';
import type { IntlShape } from 'react-intl';
import { navV3Messages } from '../nav-v3.messages';
import type { RecentItem } from './merge-recent-items';

export type RecentsDateGroup = 'today' | 'yesterday' | 'last7' | 'older';

export const RECENTS_DATE_GROUP_ORDER: readonly RecentsDateGroup[] = [
  'today',
  'yesterday',
  'last7',
  'older',
];

export interface GroupedRecentItems {
  group: RecentsDateGroup;
  items: RecentItem[];
}

/** Buckets a calendar date relative to `now`. Last 7 days covers days 2–7 ago. */
export function getRecentsDateGroup(date: Date, now: Date): RecentsDateGroup {
  const dayDelta = differenceInCalendarDays(date, now);
  if (dayDelta >= 0) return 'today';
  if (dayDelta === -1) return 'yesterday';
  if (dayDelta >= -7 && dayDelta <= -2) return 'last7';
  return 'older';
}

/** Date for browse section headers — `created_at` for visits and chats. */
export function getRecentItemBucketDate(item: RecentItem): Date | null {
  const createdAt = item.kind === 'visit' ? item.visit.created_at : item.chat.created_at;
  return parseTimeStampOrNull(createdAt, true);
}

export function getRecentsDateGroupForItem(
  item: RecentItem,
  now: Date
): RecentsDateGroup {
  const date = getRecentItemBucketDate(item);
  if (!date) return 'older';
  return getRecentsDateGroup(date, now);
}

export function getRecentsDateGroupLabel(
  group: RecentsDateGroup,
  intl: IntlShape
): string {
  switch (group) {
    case 'today':
      return intl.formatMessage(navV3Messages.recentsGroupToday);
    case 'yesterday':
      return intl.formatMessage(navV3Messages.recentsGroupYesterday);
    case 'last7':
      return intl.formatMessage(navV3Messages.recentsGroupLast7);
    case 'older':
      return intl.formatMessage(navV3Messages.recentsGroupOlder);
  }
}

/**
 * Splits a reverse-chron merged list into contiguous date sections. Items
 * without a usable date are placed in `older`. Empty sections are omitted.
 */
export function groupRecentItemsByDate(
  items: readonly RecentItem[],
  now: Date
): GroupedRecentItems[] {
  const buckets = new Map<RecentsDateGroup, RecentItem[]>(
    RECENTS_DATE_GROUP_ORDER.map((group) => [group, []])
  );

  for (const item of items) {
    const group = getRecentsDateGroupForItem(item, now);
    buckets.get(group)?.push(item);
  }

  return RECENTS_DATE_GROUP_ORDER.flatMap((group) => {
    const groupItems = buckets.get(group) ?? [];
    return groupItems.length > 0 ? [{ group, items: groupItems }] : [];
  });
}
